import React, { useState, useMemo, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import {
  fetchBakingProductionLogs,
  createBakingProductionLogAsync,
  updateBakingProductionLogAsync,
  deleteBakingProductionLogAsync,
  createBlankBakingProductionLog
} from '../../../store/slices/bakingProductionLogSlice';
import { fetchBatchRecordById, deleteBatchRecord } from '../../../store/slices/batchRecordsSlice';
import { addTraceCode, removeTraceCode } from '../../../store/slices/traceCodesSlice';
import { BakingProductionLog } from '../../../types/bakingProductionLog';
import bakingProductionLogApiService from '../../../services/bakingProductionLogApiService';
import { groupItemsBySection, getDefaultExpandedState } from '../../../utils/haccpSectionUtils';
import { HaccpSectionHeader } from '../../../components/haccp/HaccpSectionHeader';
import { ClickableIdentifier } from '../../../components/haccp/ClickableIdentifier';
import { useTraceability } from '../../../components/haccp/TraceabilityContext';
import { matchesSearchQuery } from '../../../utils/searchUtils';
import { scrollToFieldAndHighlight } from '../../../utils/scrollUtils';

interface BakingProductionLogTableProps {
  targetBakingId?: string;
  targetField?: string;
  isFlagged?: boolean;
}

export const BakingProductionLogTable: React.FC<BakingProductionLogTableProps> = ({
  targetBakingId,
  targetField,
  isFlagged
}) => {
  const dispatch = useAppDispatch();
  const { openTraceability } = useTraceability();
  const logs = useAppSelector(state => state.bakingProductionLog.logs);
  const fillings = useAppSelector(state => state.recipes.fillings);
  const loading = useAppSelector(state => state.bakingProductionLog.loading);
  const error = useAppSelector(state => state.bakingProductionLog.error);

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingLogId, setEditingLogId] = useState<string | null>(null);

  // Form state for new entry
  const [newLog, setNewLog] = useState<BakingProductionLog>(createBlankBakingProductionLog());

  // Form state for editing
  const [editLog, setEditLog] = useState<BakingProductionLog | null>(null);

  // Filter logs based on search
  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) return logs;
    return logs.filter(log => matchesSearchQuery(log, searchTerm, [log.fillingName || '']));
  }, [logs, searchTerm]);

  // Group logs into sections by date (extracted from startTime)
  const sections = useMemo(
    () => groupItemsBySection(filteredLogs, (log) => log.startTime),
    [filteredLogs]
  );

  // Track expanded/collapsed state for each section
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    getDefaultExpandedState(sections)
  );

  // Auto-expand section containing target log for deep-linking
  useEffect(() => {
    if (!targetBakingId) return;
    for (const section of sections) {
      if (section.items.some(log => log.id === targetBakingId)) {
        setExpandedSections(prev => ({ ...prev, [section.key]: true }));
        break;
      }
    }
  }, [targetBakingId, sections]);

  // Field-level deep-linking from admin review
  useEffect(() => {
    if (!targetBakingId || !targetField) return;
    const timer = setTimeout(() => {
      scrollToFieldAndHighlight(`baking-${targetBakingId}-field-${targetField}`, {
        highlightColor: isFlagged ? 'red' : 'blue'
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [targetBakingId, targetField, isFlagged]);

  // Update expanded state when sections change
  useEffect(() => {
    setExpandedSections((prev) => {
      const newState = { ...prev };
      let hasChanges = false;
      for (const section of sections) {
        if (!(section.key in newState)) {
          newState[section.key] = false;
          hasChanges = true;
        }
      }
      return hasChanges ? newState : prev;
    });
  }, [sections]);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  /**
   * Format date as YYYYMMDD for lot number generation
   */
  const formatDateForLotNumber = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  /**
   * Generate lot number when product is selected
   */
  const handleProductSelection = async (fillingId: string, isEditMode: boolean = false) => {
    if (!fillingId) {
      // Clear lot number if no product selected
      if (isEditMode && editLog) {
        setEditLog({ ...editLog, lotNumber: '' });
      } else {
        setNewLog({ ...newLog, lotNumber: '' });
      }
      return;
    }

    // Find the selected filling to get product code
    const selectedFilling = fillings.find(f => f.id === fillingId);
    if (!selectedFilling || !selectedFilling.productCode) {
      console.warn('Selected filling does not have a product code');
      return;
    }

    try {
      // Get today's date in YYYYMMDD format
      const today = new Date();
      const dateStr = formatDateForLotNumber(today);

      // Generate lot number
      const lotNumber = await bakingProductionLogApiService.generateLotNumber(
        selectedFilling.productCode,
        dateStr
      );

      // Update state with generated lot number
      if (isEditMode && editLog) {
        setEditLog({ ...editLog, fillingId, lotNumber });
      } else {
        setNewLog({ ...newLog, fillingId, lotNumber });
      }
    } catch (error) {
      console.error('Error generating lot number:', error);
      alert('Failed to generate lot number. Please try again.');
    }
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewLog(createBlankBakingProductionLog());
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewLog(createBlankBakingProductionLog());
  };

  const validateLog = (log: BakingProductionLog): string | null => {
    // Required fields only
    if (!log.fillingId) return 'Please select a product';
    if (!log.lotNumber.trim()) return 'Please enter a lot number';
    if (!log.startTime) return 'Please enter a start time';
    if (!log.ovenTemp || log.ovenTemp <= 0) return 'Please enter a valid oven temperature';

    // Optional finish time validation - only if provided
    if (log.finishTime && log.startTime) {
      if (new Date(log.finishTime) <= new Date(log.startTime)) {
        return 'Finish time must be after start time';
      }
    }

    return null;
  };

  const handleSaveNew = async () => {
    const validationError = validateLog(newLog);
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      const result = await dispatch(createBakingProductionLogAsync(newLog)).unwrap();

      // Fetch the newly created batch record to update Redux state
      if (result.batchRecordId) {
        try {
          await dispatch(fetchBatchRecordById(result.batchRecordId)).unwrap();
        } catch (batchErr: any) {
          // Log error but don't block the user - the log was already created successfully
          console.error('Failed to fetch batch record:', batchErr);
        }
      }

      // Add the auto-created trace code to Redux state
      if (result.traceCode) {
        dispatch(addTraceCode(result.traceCode));
      }

      handleCancelAdd();
    } catch (err: any) {
      console.error('Failed to create baking production log:', err);
      alert(err.message || 'Failed to add log. Please try again.');
    }
  };

  const handleEdit = (log: BakingProductionLog) => {
    setEditingLogId(log.id);
    setEditLog({ ...log });
  };

  const handleCancelEdit = () => {
    setEditingLogId(null);
    setEditLog(null);
  };

  const handleSaveEdit = async () => {
    if (!editLog) return;

    const validationError = validateLog(editLog);
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      await dispatch(updateBakingProductionLogAsync(editLog)).unwrap();
      handleCancelEdit();
    } catch (err: any) {
      console.error('Failed to update baking production log:', err);
      alert(err.message || 'Failed to update log. Please try again.');
    }
  };

  const handleDelete = async (id: string, lotNumber: string) => {
    if (window.confirm(
      `Are you sure you want to delete log for lot ${lotNumber}?\n\n` +
      `This will also delete the associated batch record and trace code.`
    )) {
      try {
        const result = await dispatch(deleteBakingProductionLogAsync(id)).unwrap();

        // Remove cascade-deleted batch record from Redux state
        if (result.batchRecordId) {
          dispatch(deleteBatchRecord(result.batchRecordId));
        }

        // Remove cascade-deleted trace code from Redux state
        if (result.traceCodeId) {
          dispatch(removeTraceCode(result.traceCodeId));
        }
      } catch (err: any) {
        console.error('Failed to delete baking production log:', err);
        alert(err.message || 'Failed to delete log. Please try again.');
      }
    }
  };

  const formatDateTimeForInput = (isoString: string): string => {
    if (!isoString) return '';
    return isoString.slice(0, 16); // YYYY-MM-DDTHH:MM
  };

  const formatDateTimeForDisplay = (isoString: string): string => {
    if (!isoString) return '';

    // Parse as local time (YYYY-MM-DDTHH:MM format)
    const [datePart, timePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    // Create date in local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day, hour, minute);

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && logs.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
        Loading logs...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        backgroundColor: '#fff3cd',
        color: '#856404',
        borderRadius: '4px',
        border: '1px solid #ffeaa7',
        fontSize: '14px'
      }}>
        Error: {error}
      </div>
    );
  }

  // Table header component to avoid repetition
  const renderTableHeader = () => (
    <thead>
      <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Product *
        </th>
        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Lot # *
        </th>
        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Start Time *
        </th>
        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Finish Time
        </th>
        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '100px' }}>
          Oven Temp (°F) *
        </th>
        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '100px' }}>
          Staff
        </th>
        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Corrective Action
        </th>
        <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '120px' }}>
          Actions
        </th>
      </tr>
    </thead>
  );

  // Render a single log row (edit or display mode)
  const renderLogRow = (log: BakingProductionLog) => {
    if (editingLogId === log.id && editLog) {
      return (
        <tr key={log.id} style={{ backgroundColor: '#fffef0' }}>
          <td style={{ padding: '12px' }}>
            <select
              value={editLog.fillingId}
              onChange={(e) => handleProductSelection(e.target.value, true)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            >
              <option value="">Select product...</option>
              {fillings.map(filling => (
                <option key={filling.id} value={filling.id}>{filling.name}</option>
              ))}
            </select>
          </td>
          <td style={{ padding: '12px' }}>
            <input
              type="text"
              value={editLog.lotNumber}
              readOnly
              disabled
              placeholder="Auto-generated"
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </td>
          <td style={{ padding: '12px' }}>
            <input
              type="datetime-local"
              value={formatDateTimeForInput(editLog.startTime)}
              onChange={(e) => setEditLog({ ...editLog, startTime: e.target.value })}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            />
          </td>
          <td style={{ padding: '12px' }}>
            <input
              type="datetime-local"
              value={formatDateTimeForInput(editLog.finishTime)}
              onChange={(e) => setEditLog({ ...editLog, finishTime: e.target.value })}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            />
          </td>
          <td style={{ padding: '12px' }}>
            <input
              type="number"
              value={editLog.ovenTemp}
              onChange={(e) => setEditLog({ ...editLog, ovenTemp: parseInt(e.target.value) || 0 })}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            />
          </td>
          <td style={{ padding: '12px' }}>
            <input
              type="text"
              value={editLog.staffInitials}
              onChange={(e) => setEditLog({ ...editLog, staffInitials: e.target.value })}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            />
          </td>
          <td style={{ padding: '12px' }}>
            <input
              type="text"
              value={editLog.correctiveAction || ''}
              onChange={(e) => setEditLog({ ...editLog, correctiveAction: e.target.value })}
              placeholder="Optional"
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
            />
          </td>
          <td style={{ padding: '12px', textAlign: 'center' }}>
            <button
              onClick={handleSaveEdit}
              style={{ padding: '6px 12px', marginRight: '4px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
            >
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              style={{ padding: '6px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
            >
              Cancel
            </button>
          </td>
        </tr>
      );
    }

    return (
      <tr key={log.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
        <td id={`baking-${log.id}-field-fillingName`} style={{ padding: '12px', fontSize: '14px' }}>{log.fillingName || 'Unknown'}</td>
        <td id={`baking-${log.id}-field-lotNumber`} style={{ padding: '12px', fontSize: '14px' }}>
          {log.lotNumber ? (
            <ClickableIdentifier value={log.lotNumber} type="batchLotNumber" onClick={openTraceability} />
          ) : '-'}
        </td>
        <td id={`baking-${log.id}-field-startTime`} style={{ padding: '12px', fontSize: '14px' }}>{formatDateTimeForDisplay(log.startTime)}</td>
        <td id={`baking-${log.id}-field-finishTime`} style={{ padding: '12px', fontSize: '14px', color: log.finishTime ? '#000' : '#999', fontStyle: log.finishTime ? 'normal' : 'italic' }}>
          {log.finishTime ? formatDateTimeForDisplay(log.finishTime) : 'Pending'}
        </td>
        <td id={`baking-${log.id}-field-ovenTemp`} style={{ padding: '12px', fontSize: '14px' }}>{log.ovenTemp}°F</td>
        <td id={`baking-${log.id}-field-staffInitials`} style={{ padding: '12px', fontSize: '14px', color: log.staffInitials ? '#000' : '#999', fontStyle: log.staffInitials ? 'normal' : 'italic' }}>
          {log.staffInitials || 'Not assigned'}
        </td>
        <td id={`baking-${log.id}-field-correctiveAction`} style={{ padding: '12px', fontSize: '14px', color: log.correctiveAction ? '#856404' : '#999', fontStyle: log.correctiveAction ? 'normal' : 'italic' }}>
          {log.correctiveAction || 'None'}
        </td>
        <td style={{ padding: '12px', textAlign: 'center' }}>
          <button
            onClick={() => handleEdit(log)}
            style={{ padding: '6px 12px', marginRight: '4px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(log.id, log.lotNumber)}
            style={{ padding: '6px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
          >
            Delete
          </button>
        </td>
      </tr>
    );
  };

  // Render add new row
  const renderAddNewRow = () => (
    <tr style={{ backgroundColor: '#f9f9f9' }}>
      <td style={{ padding: '12px' }}>
        <select
          value={newLog.fillingId}
          onChange={(e) => handleProductSelection(e.target.value, false)}
          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
        >
          <option value="">Select product...</option>
          {fillings.map(filling => (
            <option key={filling.id} value={filling.id}>{filling.name}</option>
          ))}
        </select>
      </td>
      <td style={{ padding: '12px' }}>
        <input
          type="text"
          value={newLog.lotNumber}
          readOnly
          disabled
          placeholder="Auto-generated"
          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
        />
      </td>
      <td style={{ padding: '12px' }}>
        <input
          type="datetime-local"
          value={formatDateTimeForInput(newLog.startTime)}
          onChange={(e) => setNewLog({ ...newLog, startTime: e.target.value })}
          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
        />
      </td>
      <td style={{ padding: '12px' }}>
        <input
          type="datetime-local"
          value={formatDateTimeForInput(newLog.finishTime)}
          onChange={(e) => setNewLog({ ...newLog, finishTime: e.target.value })}
          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
        />
      </td>
      <td style={{ padding: '12px' }}>
        <input
          type="number"
          value={newLog.ovenTemp}
          onChange={(e) => setNewLog({ ...newLog, ovenTemp: parseInt(e.target.value) || 0 })}
          placeholder="350"
          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
        />
      </td>
      <td style={{ padding: '12px' }}>
        <input
          type="text"
          value={newLog.staffInitials}
          onChange={(e) => setNewLog({ ...newLog, staffInitials: e.target.value })}
          placeholder="Initials"
          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
        />
      </td>
      <td style={{ padding: '12px' }}>
        <input
          type="text"
          value={newLog.correctiveAction || ''}
          onChange={(e) => setNewLog({ ...newLog, correctiveAction: e.target.value })}
          placeholder="Optional"
          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
        />
      </td>
      <td style={{ padding: '12px', textAlign: 'center' }}>
        <button
          onClick={handleSaveNew}
          style={{ padding: '6px 12px', marginRight: '4px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
        >
          Save
        </button>
        <button
          onClick={handleCancelAdd}
          style={{ padding: '6px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
        >
          Cancel
        </button>
      </td>
    </tr>
  );

  return (
    <div>
      {/* Add button */}
      {!isAddingNew && (
        <button
          onClick={handleAddNew}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '16px'
          }}
        >
          + Add Baking Production Log
        </button>
      )}

      {/* Add new form (shown as standalone table when adding) */}
      {isAddingNew && (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '16px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            {renderTableHeader()}
            <tbody>
              {renderAddNewRow()}
            </tbody>
          </table>
        </div>
      )}

      {/* Search */}
      {!isAddingNew && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Search baking production logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              boxSizing: 'border-box' as const
            }}
          />
        </div>
      )}

      {/* Empty state */}
      {logs.length === 0 && !isAddingNew && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
          No baking production logs yet. Click "Add Baking Production Log" to create one.
        </div>
      )}

      {/* No search results */}
      {logs.length > 0 && filteredLogs.length === 0 && !isAddingNew && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
          No logs match your search for "{searchTerm}".
        </div>
      )}

      {/* Sections with logs */}
      {sections.map((section) => (
        <div key={section.key} style={{ marginBottom: '16px' }}>
          <HaccpSectionHeader
            label={section.label}
            itemCount={section.items.length}
            isExpanded={expandedSections[section.key] ?? !section.isTrialSection}
            isTrialSection={section.isTrialSection}
            onToggle={() => toggleSection(section.key)}
          />

          {(expandedSections[section.key] ?? !section.isTrialSection) && (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                {renderTableHeader()}
                <tbody>
                  {section.items.map((log) => renderLogRow(log))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
